import { Fragment, KeyboardEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  CaretDownIcon,
  FunctionIcon,
  GlobeIcon,
  LightbulbIcon,
  MagnifyingGlassIcon,
  XIcon,
  type Icon
} from "@phosphor-icons/react";
import { cn } from "@/zapi-frontend/shared/lib/utils";

export type TLedgerKind = "sourced" | "calculated" | "assumption";

const KINDS: Record<TLedgerKind, { icon: Icon; tile: string }> = {
  sourced: {
    icon: GlobeIcon,
    tile: "bg-greyscale-100 text-greyscale-700 dark:bg-greyscale-800 dark:text-greyscale-200"
  },
  calculated: {
    icon: FunctionIcon,
    tile: "bg-secondary-100 text-secondary-700 dark:bg-secondary-900 dark:text-secondary-200"
  },
  assumption: {
    icon: LightbulbIcon,
    tile: "bg-primary-100 text-primary-800 dark:bg-primary-950 dark:text-primary-300"
  }
};

export const BODY_TEXT = "typo-body-sm text-muted-foreground";
export const LINK =
  "inline-flex min-w-0 items-center gap-1.5 text-primary-text underline-offset-2 hover:underline [&_svg]:size-3.5 [&_svg]:shrink-0";
export const FIGURE_LINK =
  "typo-body-sm rounded-sm text-left text-primary-text underline-offset-2 outline-none hover:underline focus-visible:ring-3 focus-visible:ring-ring/50";

const ROW_GRID = "grid items-center gap-x-4 px-5";
const WIDE_COLUMNS = "grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_6rem_8.5rem_2rem]";
const NARROW_COLUMNS = "grid-cols-[minmax(0,1fr)_auto_2rem]";

export function LedgerSearch({
  value,
  onChange,
  placeholder,
  testId,
  children
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  testId?: string;
  children?: ReactNode;
}) {
  const { t } = useTranslation("projectHome");
  return (
    <div className="flex h-10 w-80 min-w-0 items-center rounded-full border border-border bg-card px-1 shadow-xs transition-colors focus-within:border-primary">
      <MagnifyingGlassIcon className="ms-3 size-[18px] shrink-0 text-muted-foreground" aria-hidden="true" />
      <input
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape" && value) {
            event.preventDefault();
            onChange("");
          }
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        data-testid={testId}
        className="typo-body-sm h-full min-w-0 flex-1 bg-transparent px-2 text-foreground outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:appearance-none"
      />
      {value && (
        <button
          type="button"
          aria-label={t("modelLedger.clearSearch")}
          onClick={() => onChange("")}
          className="flex size-6 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
        >
          <XIcon className="size-3.5" />
        </button>
      )}
      {children && <div className="flex shrink-0 items-center gap-1 pe-0.5 ps-1">{children}</div>}
    </div>
  );
}

export function LedgerColumns({ labels, narrow }: { labels: [string, string, string, string]; narrow: boolean }) {
  if (narrow) return null;
  return (
    <div
      aria-hidden="true"
      className={cn(ROW_GRID, WIDE_COLUMNS, "typo-overline border-b border-foreground/5 py-2.5 text-muted-foreground")}
    >
      <span>{labels[0]}</span>
      <span>{labels[1]}</span>
      <span>{labels[2]}</span>
      <span className="text-end">{labels[3]}</span>
      <span />
    </div>
  );
}

export function LedgerGroup({ name, children }: { name: string; children: ReactNode }) {
  return (
    <section aria-label={name}>
      <h3 className="typo-overline bg-muted/40 px-5 py-2 text-muted-foreground">{name}</h3>
      <ul>{children}</ul>
    </section>
  );
}

function KindTile({ kind, label }: { kind: TLedgerKind; label: string }) {
  const { icon: KindIcon, tile } = KINDS[kind];
  return (
    <span className={cn("flex size-7 shrink-0 items-center justify-center rounded-md", tile)} title={label}>
      <KindIcon weight="bold" className="size-3.5" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

export function LedgerRow({
  rowKey,
  testId,
  open,
  highlighted,
  narrow,
  kind,
  kindLabel,
  name,
  description,
  source,
  role,
  value,
  panel,
  onToggle
}: {
  rowKey: string;
  testId: string;
  open: boolean;
  highlighted: boolean;
  narrow: boolean;
  kind: TLedgerKind;
  kindLabel: string;
  name: string;
  description: string | null;
  source: ReactNode;
  role: ReactNode;
  value: ReactNode;
  panel: ReactNode;
  onToggle: () => void;
}) {
  const toggleOnKey = (event: KeyboardEvent) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    onToggle();
  };
  return (
    <li data-ledger-key={rowKey} className="border-t border-foreground/5 first:border-t-0">
      <div
        role="button"
        tabIndex={0}
        aria-expanded={open}
        data-testid={testId}
        data-highlighted={highlighted || undefined}
        onClick={onToggle}
        onKeyDown={toggleOnKey}
        className={cn(
          ROW_GRID,
          narrow ? NARROW_COLUMNS : WIDE_COLUMNS,
          "cursor-pointer py-3 outline-none transition-colors duration-500 hover:bg-muted/40 focus-visible:bg-muted/40 data-highlighted:bg-primary-50"
        )}
      >
        <div className="flex min-w-0 items-center gap-3">
          <KindTile kind={kind} label={kindLabel} />
          <div className="flex min-w-0 flex-col">
            <span className="typo-label-md line-clamp-2 text-foreground" title={name}>
              {name}
            </span>
            {description && (
              <span className="typo-caption truncate text-muted-foreground" title={description}>
                {description}
              </span>
            )}
          </div>
        </div>
        {!narrow && <div className="min-w-0">{source}</div>}
        {!narrow && <div className="min-w-0">{role}</div>}
        <div className="typo-label-md flex flex-col items-end text-end text-foreground">{value}</div>
        <span
          className={cn(
            "flex size-8 items-center justify-center justify-self-center rounded-full text-muted-foreground transition-colors",
            open && "bg-muted text-foreground"
          )}
        >
          <CaretDownIcon
            weight="bold"
            className={cn("size-3 transition-transform duration-200", open && "rotate-180")}
          />
        </span>
      </div>
      {panel}
    </li>
  );
}

export function LedgerPanel({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <div className="mx-5 mb-4 flex flex-col gap-5 rounded-xl bg-muted/50 p-4" data-testid={testId}>
      {children}
    </div>
  );
}

export function LedgerPanelSection({
  heading,
  children,
  testId
}: {
  heading: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <section className="flex min-w-0 flex-col gap-2" data-testid={testId}>
      <h4 className="typo-overline text-muted-foreground">{heading}</h4>
      {children}
    </section>
  );
}

export function LedgerFacts({ facts }: { facts: [string, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[max-content_minmax(0,1fr)] items-baseline gap-x-6 gap-y-1.5">
      {facts.map(([label, value]) => (
        <Fragment key={label}>
          <dt className="typo-body-sm text-muted-foreground">{label}</dt>
          <dd className="typo-body-sm min-w-0 text-foreground">{value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

export function LedgerPanelDivider() {
  return <div className="h-px w-full bg-foreground/5" />;
}
